// src/ClinicalDataCollector/components/output/ClinicalSummary.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Box,
  Tabs,
  Tab,
  Stack,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useSelector } from "react-redux";

import MedicationsSummary from "./MedicationsSummary";
import LabsSummary from "./LabsSummary";
import AllergiesSummary from "./AllergiesSummary";

import AnnualWellnessSummary from "./AnnualWellnessSummary";
import TelehealthSummary from "./TelehealthSummary";
import HospitalDischargeFollowUpSummary from "./HospitalDischargeFollowUpSummary";
import FollowUpExamSummary from "./FollowUpExamSummary";

import { CodingOpContext } from "../../../../context/CodingOpContext/CodingOpContext";
import { CareGapContext } from "../../../../context/PatientContext/CareGapContext/CareGapContext"; 

// ===================== helpers =====================
function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const safeText = (v, fallback = "—") =>
  v === null || v === undefined || v === "" ? fallback : String(v);

function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function StatusPill({ text, variant = "outlined" }) {
  if (!text) return null;
  return <Chip size="small" label={text} variant={variant} sx={{ borderRadius: 2 }} />;
}

export default function ClinicalSummary() {
  // 0 = Medications, 1 = Labs, 2 = Clinical Notes, 3 = Coding Opps, 4 = Care Gaps
  const [tab, setTab] = useState(0);

  // Paciente (para sacar cipId)
  const PatientSelect = useSelector((state) => {
    return (
      state?.patientMapSelect?.PatientSelect ??
      state?.patientMapSelect?.patientSelect ??
      null
    );
  });

  const cipId = useMemo(() => {
    const v =
      PatientSelect?.cipId ??
      PatientSelect?.CipId ??
      PatientSelect?.cipID ??
      PatientSelect?.cip ??
      "";
    return String(v || "").trim();
  }, [PatientSelect]);

  // ===================== Coding Opportunities context =====================
  const codingCtx = useContext(CodingOpContext);
  const codingOps = codingCtx?.codingOps || [];
  const codingLoading = Boolean(codingCtx?.loading);
  const codingError = codingCtx?.error;
  const loadCodingOpportunitiesByPatient = codingCtx?.loadCodingOpportunitiesByPatient;

  // ===================== Care Gaps context =====================
  const careGapCtx = useContext(CareGapContext);
  const careGaps = careGapCtx?.careGapDetails || [];
  const careGapLoading = Boolean(careGapCtx?.detailsLoading);
  const fetchCareGapDetails = careGapCtx?.fetchCareGapDetails;

  // Evitar recargas innecesarias por tab switch
  const lastLoadedRef = useRef({ codingCip: "", careGapCip: "" });

  // Lazy-load al entrar a tabs 3/4
  useEffect(() => {
    if (!cipId) return;

    // Coding Opps tab
    if (tab === 3) {
      const canLoad = typeof loadCodingOpportunitiesByPatient === "function";
      if (canLoad && lastLoadedRef.current.codingCip !== cipId) {
        lastLoadedRef.current.codingCip = cipId;
        loadCodingOpportunitiesByPatient(cipId, { pageSize: 200 });
      }
    }

    // Care Gaps tab
    if (tab === 4) {
      const canLoad = typeof fetchCareGapDetails === "function";
      if (canLoad && lastLoadedRef.current.careGapCip !== cipId) {
        lastLoadedRef.current.careGapCip = cipId;
        fetchCareGapDetails(cipId);
      }
    }
  }, [tab, cipId, loadCodingOpportunitiesByPatient, fetchCareGapDetails]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2, mb: 4, overflow: "hidden" }}>
      <CardHeader
        title="Clinical Summary"
        titleTypographyProps={{ variant: "h6", fontWeight: 900 }}
        sx={{ pb: 1.25 }}
      />
      <Divider />

      <CardContent sx={{ pt: 1.5 }}>
        {/* ✅ Demographics fuera (ya está en Patient card) */}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Medications" />
            <Tab label="Labs" />
            <Tab label="Clinical Notes" />
            <Tab label="Coding Opportunities" />
            <Tab label="Care Gaps" />
          </Tabs>
        </Box>

        {/* ===================== Medications ===================== */}
        <TabPanel value={tab} index={0}>
          <Stack spacing={2}>
            <MedicationsSummary />
          </Stack>
        </TabPanel>

        {/* ===================== Labs ===================== */}
        <TabPanel value={tab} index={1}>
          <Stack spacing={2}>
            <LabsSummary />
            <AllergiesSummary />
          </Stack>
        </TabPanel>

        {/* ===================== Clinical Notes ===================== */}
        <TabPanel value={tab} index={2}>
          <Stack spacing={2}>
            <FollowUpExamSummary />
            <AnnualWellnessSummary />
            <TelehealthSummary />
            <HospitalDischargeFollowUpSummary />
          </Stack>
        </TabPanel>

        {/* ===================== Coding Opportunities ===================== */}
        <TabPanel value={tab} index={3}>
          <Stack spacing={1.5}>
            {!cipId ? (
              <Typography variant="body2" color="text.secondary">
                No cipId found for this patient.
              </Typography>
            ) : null}

            {codingLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading coding opportunities…
                </Typography>
              </Stack>
            ) : null}

            {codingError ? (
              <Typography variant="body2" color="error">
                Error loading coding opportunities.
              </Typography>
            ) : null}

            {!codingLoading && !codingError && Array.isArray(codingOps) && codingOps.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 900 }}>HCC</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Diagnosis</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Year</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Initial DOS</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Last DOS</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>PCP</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {codingOps.map((row) => (
                      <TableRow key={row.id || row.codingOpportunityId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={900}>
                            {safeText(row.hccId)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {safeText(row.hccDescription)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={800}>
                            {safeText(row.diagnosisCode)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {safeText(row.diagnosisDescription)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusPill text={row.statusName || row.status} />
                        </TableCell>
                        <TableCell>{safeText(row.measureYear)}</TableCell>
                        <TableCell>{formatDate(row.initialDos)}</TableCell>
                        <TableCell>{formatDate(row.lastDos)}</TableCell>
                        <TableCell>{safeText(row.pcpName)}</TableCell>
                        <TableCell>{safeText(row.medicalCenter)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}

            {!codingLoading && !codingError && (!Array.isArray(codingOps) || codingOps.length === 0) ? (
              <Typography variant="body2" color="text.secondary">
                No coding opportunities found for this patient.
              </Typography>
            ) : null}
          </Stack>
        </TabPanel>

        {/* ===================== Care Gaps ===================== */}
        <TabPanel value={tab} index={4}>
          <Stack spacing={1.5}>
            {!cipId ? (
              <Typography variant="body2" color="text.secondary">
                No cipId found for this patient.
              </Typography>
            ) : null}

            {careGapLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading care gaps…
                </Typography>
              </Stack>
            ) : null}

            {!careGapLoading && Array.isArray(careGaps) && careGaps.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 900 }}>Care Gap</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Source</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Workflow</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Submitted</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Closure</TableCell>
                      <TableCell sx={{ fontWeight: 900, textAlign: "right" }}>Evidence</TableCell>
                      <TableCell sx={{ fontWeight: 900, textAlign: "right" }}>Outreach</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {careGaps.map((row) => (
                      <TableRow key={row.id || row.careGapId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={900}>
                            {safeText(row.careGapName)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {safeText(row.careGapId || row.id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <StatusPill text={row.sourceStatusCode || row.sourceStatus} />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <StatusPill text={row.workflowStatusCode || row.workflowStatus} />
                          </Stack>
                        </TableCell>
                        <TableCell>{formatDate(row.submittedDate)}</TableCell>
                        <TableCell>{formatDate(row.serviceClosureDate)}</TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          {safeText(row.evidenceCount, "0")}
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          {safeText(row.outreachCount, "0")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}

            {!careGapLoading && (!Array.isArray(careGaps) || careGaps.length === 0) ? (
              <Typography variant="body2" color="text.secondary">
                No care gaps found for this patient.
              </Typography>
            ) : null}
          </Stack>
        </TabPanel>
      </CardContent>
    </Card>
  );
}
